
import {
  LogType
} from "../../common/schemas/enums";
import {
  LogManager
} from './log-manager';

export class CustomTimeout{
  private _name: string;
  private id: number;
  private _createdTime: number;
  private startTime: number;
  private remainingTime: number;
  private millisecondsTimeout: number;
  private elapsedTotalTime: number;
  private _repeatCount: number = 0;
  private callbackFunction: Function;
  private logManager:LogManager;
  private completeCallback: Function = this.complete.bind(this);
  private defaultLogType: LogType = LogType.QA;

  constructor(callback: Function, milliseconds:number, name, lm?:LogManager, lt?:LogType){
    this.id = (milliseconds > 0)?setTimeout(this.completeCallback, milliseconds):null;
    this._createdTime = (new Date()).getTime();
    this.startTime = this._createdTime;
    this.remainingTime = milliseconds;
    this.millisecondsTimeout = milliseconds;
    this.elapsedTotalTime = 0;
    this.callbackFunction = callback;
    this.logManager = lm ?? null;
    this.defaultLogType = lt ?? this.defaultLogType;
    this._name = name;
    this.debugLog(`Timer '${this.name}' created with ${milliseconds/1000} seconds until callback.`);
  }

  pause(): number{
    if(typeof this.id !== 'undefined' && this.id !== null){
        clearTimeout(this.id);
        const timeChanged = this.timeElapsedSinceStartTime;
        this.debugLog(`Timer ${this.name} timeChanged value: ${timeChanged}ms, total time is ${this.getTotalElapsedTime()}ms`);
        this.remainingTime -= timeChanged;
        this.elapsedTotalTime += timeChanged;
        this.id = null;
        this.debugLog(`Timer '${this.name}' paused with ${this.remainingTime/1000} seconds remaining.`);
        return this.remainingTime;
    }
    if(this.remainingTime > 0){
      this.debugLog(`Timer '${this.name}' | id=${this.id} unable to pause.`);
    }else{
      this.debugLog(`Timer '${this.name}' not active - unable to pause.`);
    }
    return 0;
  }

  resume(): number{
    if(this.id === null && this.remainingTime > 0){
        this.id = setTimeout(this.completeCallback, this.remainingTime);
        this.startTime = (new Date()).getTime();
        this.debugLog(`Timer '${this.name}' resumed with ${this.remainingTime/1000} seconds remaining.`);
        return this.remainingTime;
    }
    if(this.remainingTime > 0){
      this.debugLog(`Timer '${this.name}' | id=${this.id} unable to resume.`);
    }else{
      this.debugLog(`Timer '${this.name}' not active - unable to resume.`);
    }
    return 0;
  }

  cancel(){
    if(this.id !== null){
      this.elapsedTotalTime += this.timeElapsedSinceStartTime, 'cancel';
      clearTimeout(this.id);
      this.id = null;
      this.remainingTime = 0;
      let repeatLog = '';
      if(this.repeatCount > 0){
        repeatLog = ` ${this.repeatCount} timeout repetitions remaining. Will reset repeat count to 0.`
        this.repeatCount = 0;
      }
      this.debugLog(`Timer '${this.name}' cancelled.${repeatLog}`);
    }
  }

  complete(){
    const timeChanged = this.timeElapsedSinceStartTime;
    this.elapsedTotalTime += timeChanged;
    this.id = null;
    if(this.remainingTime > 0){
      this.debugLog(`Timer '${this.name}' completed in ${timeChanged/1000} seconds -- ${((new Date()).getTime() - this._createdTime)/1000} seconds from Timer creation.`);
    }
    this.remainingTime = 0;
    if(this.repeatCount-- > 0){
      this.debugLog(`Timer '${this.name}' set to repeat ${this.repeatCount+1} times. Resetting timer with a ${this.millisecondsTimeout}ms callback.`);
      this.reset(this.millisecondsTimeout);
    }
    this.callbackFunction();
  }

  reset(milliseconds: number, updatedCallback?: Function){
    if(this.id !== null){
      this.elapsedTotalTime += this.timeElapsedSinceStartTime;
    }
    let resetLog: string = '';
    if(this.id !== null){
      const timeleft: number = this.timeRemainingInSeconds
      resetLog = ` Previous callback cancelled with ${timeleft} second(s) remaining.`
      clearTimeout(this.id);
      this.id = null;
    }
    this.remainingTime = milliseconds;
    this.millisecondsTimeout = milliseconds;
    this._createdTime = (new Date()).getTime();
    this.startTime = this._createdTime;
    this.callbackFunction = updatedCallback ?? this.callbackFunction;
    if(milliseconds <= 0){
      this.debugLog(`Timer '${this.name}' reset and to execute callback immediately.`);
      this.complete();
    }else{
      this.id = setTimeout(this.completeCallback, this.remainingTime);
      this.debugLog(`Timer '${this.name}' reset with ${milliseconds/1000} seconds until callback.${resetLog}`);
    }
  }

  resetIfInactive(milliseconds: number, updatedCallback?: Function){
    if(this.remainingTime === 0){
      this.reset(milliseconds, updatedCallback);
    }else{
      this.debugLog(`Timer '${this.name}' not reset because ${this.timeRemainingInSeconds} seconds still remains until callback.`);
    }
  }

  resetInterval(milliseconds: number, repeatCount: number, updatedCallback?: Function){
    this.repeatCount = repeatCount;
    this.reset(milliseconds, updatedCallback);
  }

  execute(){
    this.reset(0);
  }

  //relative to the current reset interval
  getElapsedTimeSinceReset(){
    const elapsedSincePause = this.timeElapsedSinceStartTime;
    //this.debugLog(`getElapsedTimeSinceReset -> ${this.millisecondsTimeout} - ${this.remainingTime} - ${elapsedSincePause}`, LogType.IMPORTANT);
    return this.millisecondsTimeout - this.remainingTime + elapsedSincePause;
  }

  //relative to the total lifetime of the instance of CustomTimeout
  getTotalElapsedTime(){
    const elapsedSincePause = this.timeElapsedSinceStartTime;
    return this.elapsedTotalTime + elapsedSincePause;
  }

  get timeRemainingInSeconds(): number{
    return (this.remainingTime - this.timeElapsedSinceStartTime)/1000;
  }

  get timeElapsedSinceStartTime(): number{
    return (this.id !== null)? (new Date()).getTime() - this.startTime : 0;
  }

  get definedCallbackInMillseconds(): number{
    return (this.millisecondsTimeout);
  }

  get name(): string{
    return this._name;
  }

  get isRunning(): boolean{
    return (this.remainingTime > 0);
  }

  addToRemainingTime(addSeconds: number){
    if(this.remainingTime > 0){
      this.remainingTime += addSeconds*1000;
    }
  }

  set repeatCount(count:number){
    this._repeatCount = count;
  }

  get repeatCount():number{
    return this._repeatCount;
  }

  get createdTime():number{
    return this._createdTime;
  }

  debugLog(l: string | any[], type: LogType | LogType[]=this.defaultLogType) {
    if(typeof this.logManager !== 'undefined' && this.logManager !== null){
        this.logManager.evaluateDebugLog(l, type);
    }
  }
}
